import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';
import { FormField, Switch } from '../../components/FormField.jsx';
import { EmptyState } from './Teams.jsx';

const emptyDraft = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  coverImageUrl: '',
  authorName: '',
  status: 'draft',
  publishedAt: '',
};

const chip = { draft: 'chip-neutral', published: 'chip-success' };

function slugifyClient(s) {
  return String(s)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120);
}

export default function NewsPosts() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('');
  const [modal, setModal] = useState(null); // null | 'create' | { id }
  const [form, setForm] = useState(emptyDraft);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);

  async function refresh() {
    const r = await Admin.newsPosts.list(filter || undefined);
    setPosts(r.posts);
  }
  useEffect(() => { refresh(); }, [filter]);

  function openCreate() {
    setForm(emptyDraft);
    setErr(null);
    setSlugTouched(false);
    setModal('create');
  }
  function openEdit(p) {
    setForm({
      title: p.title,
      slug: p.slug,
      excerpt: p.excerpt || '',
      body: p.body,
      coverImageUrl: p.coverImageUrl || '',
      authorName: p.authorName || '',
      status: p.status,
      publishedAt: p.publishedAt || '',
    });
    setErr(null);
    setSlugTouched(true);
    setModal({ id: p.id });
  }

  function updateTitle(v) {
    setForm((f) => {
      const next = { ...f, title: v };
      if (!slugTouched) next.slug = slugifyClient(v);
      return next;
    });
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || slugifyClient(form.title),
        excerpt: form.excerpt || undefined,
        body: form.body,
        coverImageUrl: form.coverImageUrl || undefined,
        authorName: form.authorName || undefined,
        status: form.status,
        publishedAt: form.publishedAt || undefined,
      };
      if (modal === 'create') await Admin.newsPosts.create(payload);
      else await Admin.newsPosts.update(modal.id, payload);
      setModal(null);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally { setBusy(false); }
  }

  async function remove(id) {
    await Admin.newsPosts.remove(id);
    await refresh();
  }

  async function togglePublish(p) {
    const next = p.status === 'published' ? 'draft' : 'published';
    await Admin.newsPosts.update(p.id, { status: next });
    await refresh();
  }

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="News posts"
        description="Club news that appears on the public site."
        action={<button onClick={openCreate} className="btn-primary btn-sm">+ New post</button>}
      />
      <div className="flex items-center gap-3 mb-5">
        <select className="input max-w-xs" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All</option>
          <option value="draft">Drafts</option>
          <option value="published">Published</option>
        </select>
        <span className="text-xs text-ink-3">{posts.length} {posts.length === 1 ? 'post' : 'posts'}</span>
      </div>

      {posts.length === 0 ? (
        <EmptyState onAction={openCreate} label="Write a post" />
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <article key={p.id} className="card">
              <div className="flex gap-4 items-start">
                {p.coverImageUrl && (
                  <img src={p.coverImageUrl} alt="" className="w-20 h-20 object-cover rounded border border-line shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={chip[p.status] || 'chip-neutral'}>{p.status}</span>
                    <h3 className="font-display text-xl truncate">{p.title}</h3>
                  </div>
                  <div className="text-xs text-ink-3 font-mono">/{p.slug}</div>
                  {p.excerpt && <p className="text-sm text-ink-2 mt-2 line-clamp-2">{p.excerpt}</p>}
                  <div className="text-[11px] text-ink-3 mt-2">
                    {p.publishedAt ? `Published ${new Date(p.publishedAt).toLocaleDateString()}` : 'Never published'}
                    {p.authorName ? ` · ${p.authorName}` : ''}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button className="btn-outline btn-xs" onClick={() => openEdit(p)}>Edit</button>
                  <button className="btn-ghost btn-xs" onClick={() => togglePublish(p)}>
                    {p.status === 'published' ? 'Unpublish' : 'Publish'}
                  </button>
                  <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirm(p)}>Delete</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal === 'create' ? 'New post' : 'Edit'}
        title={modal === 'create' ? 'Write a news post' : form.title || 'Edit'}
        size="lg"
        footer={
          <>
            <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('news-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
        <form id="news-form" onSubmit={save} className="space-y-4">
          <FormField label="Title" required>
            <input className="input" value={form.title} onChange={(e) => updateTitle(e.target.value)} required autoFocus />
          </FormField>
          <FormField label="URL slug" hint="Auto-derived from the title. Edit if you want a custom URL.">
            <input
              className="input font-mono text-xs"
              value={form.slug}
              onChange={(e) => { setForm({ ...form, slug: e.target.value }); setSlugTouched(true); }}
              placeholder="auto"
            />
          </FormField>
          <FormField label="Cover image URL" hint="Optional. Full https:// URL to an image.">
            <input className="input" value={form.coverImageUrl} onChange={(e) => setForm({ ...form, coverImageUrl: e.target.value })} />
          </FormField>
          <FormField label="Author name (optional)">
            <input className="input" value={form.authorName} onChange={(e) => setForm({ ...form, authorName: e.target.value })} />
          </FormField>
          <FormField label="Excerpt" hint="Short summary shown on listing pages. ~2 sentences.">
            <textarea className="input" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          </FormField>
          <FormField label="Body (HTML)" required hint="Use <p>, <strong>, <a>, <img>, <ul> etc. Script tags are stripped on render.">
            <textarea
              className="input font-mono text-xs"
              rows={14}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              required
              placeholder="<p>First paragraph…</p>"
            />
          </FormField>
          <div className="pt-2 border-t border-line">
            <Switch
              checked={form.status === 'published'}
              onChange={(v) => setForm({ ...form, status: v ? 'published' : 'draft' })}
              label="Published"
              hint="Unpublished drafts are hidden from the public site."
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.id)}
        title={`Delete "${confirm?.title}"?`}
        message="This removes the post entirely. Unpublish instead if you may want it back."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}
